import axios from 'axios';
import { Buffer } from 'buffer';
import { IDownloadFile } from 'models/general';

import { Services } from 'services';

/**
 * Extract image format from base64 data URL
 * @param dataUrl - Base64 encoded image data URL (e.g., 'data:image/png;base64,...')
 * @param defaultFormat - Default format if extraction fails (default: 'png')
 * @returns Image format (e.g., 'png', 'jpeg', 'jpg')
 */
const getImageFormat = (dataUrl: string, defaultFormat: string = 'png'): string => {
  try {
    const pdfMatch = dataUrl.match(/^data:application\/pdf;base64,/);
    if (pdfMatch) {
      return 'pdf';
    }
    const match = dataUrl.match(/^data:image\/(\w+);base64,/);
    return match?.[1] || defaultFormat;
  } catch {
    return defaultFormat;
  }
};

/**
 * Get MIME type from image format
 * @param format - Image format (e.g., 'png', 'jpeg', 'jpg')
 * @returns MIME type (e.g., 'image/png', 'image/jpeg')
 */
const getImageMimeType = (format: string): string => {
  // Normalize 'jpg' to 'jpeg' for MIME type
  if (format.toLowerCase() === 'pdf') {
    return 'application/pdf';
  }
  const normalizedFormat = format.toLowerCase() === 'jpg' ? 'jpeg' : format.toLowerCase();
  return `image/${normalizedFormat}`;
};

const uploadImagesWithPresignedUrls = async ({
  apiFunction,
  apiFunctionArgs,
  images, // Array<{ fileName: string, image: string }>
  languageCode,
}: {
  apiFunction: (...params: any) => Promise<any>;
  apiFunctionArgs: any[];
  images: Array<{ tempId?: string; image: string }>;
  languageCode: string;
}) => {
  try {
    // Step 1: Get presigned URLs array from API
    const response: { data: Array<{ url: string; fileId: string; fileName: string }> } = await apiFunction(
      ...apiFunctionArgs,
    );
    const fileInfos = response.data;

    // If response is not a non-empty array, throw error
    if (!Array.isArray(fileInfos) || !fileInfos.length) {
      throw new Error((response?.data as any)?.errors);
    }

    // Step 2: Attempt to upload each image, in parallel
    const uploadPromises = fileInfos.map((info, index) => {
      const imageObj = images[index];
      const imageFormat = getImageFormat(imageObj.image);
      const contentType = getImageMimeType(imageFormat);
      return axios({
        method: 'put',
        url: info.url,
        headers: { 'Content-Type': contentType },
        data: Buffer.from(imageObj.image.replace(/^data:(image\/\w+|application\/pdf);base64,/, ''), 'base64'),
      }).then(s3Response => {
        if (s3Response.status !== 200) {
          throw new Error((s3Response?.data as any)?.errors);
        }
        // Step 3: Confirm upload with backend
        return Services.confirmImageUpload(info.fileId, languageCode).then(r => ({
          id: info.fileId,
          tempId: imageObj.tempId,
          fileName: info.fileName,
          url: info.url,
        })); // return the response from confirmImageUpload along with tempId
      });
    });

    const results = await Promise.allSettled(uploadPromises);

    // Gather successes and errors
    const successes = results.filter(result => result.status === 'fulfilled').map((result: any) => result.value);

    const errors = results.filter(result => result.status === 'rejected').map((result: any) => result.reason);

    return { successes, errors };
  } catch (error) {
    throw error;
  }
};

const downloadFileFromBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Uploads raw File objects to presigned S3 URLs and confirms each with the backend.
// presignedEntries and files must be parallel arrays (same index = same file).
// Returns { successes, errors } to allow graceful handling of partial failures.
const uploadFilesWithPresignedUrls = async (
  presignedEntries: Array<{ fileId: string; url: string }>,
  files: Array<{ file: File }>,
  languageCode: string,
): Promise<{ successes: Array<{ index: number; fileId: string; fileName: string }>; errors: Error[] }> => {
  const uploadPromises = presignedEntries.map(async (entry, index) => {
    const { file } = files[index];
    try {
      await axios({ method: 'put', url: entry.url, headers: { 'Content-Type': file.type }, data: file });
      await Services.confirmImageUpload(entry.fileId, languageCode);
      // index correlates back to the caller's parallel `files` array (same index = same file).
      return { index, fileId: entry.fileId, fileName: file.name };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to upload ${file.name}: ${errorMsg}`);
    }
  });

  const results = await Promise.allSettled(uploadPromises);

  const successes = results
    .filter(result => result.status === 'fulfilled')
    .map((result: any) => result.value);

  const errors = results
    .filter(result => result.status === 'rejected')
    .map((result: any) => result.reason);

  return { successes, errors };
};

export { uploadImagesWithPresignedUrls, uploadFilesWithPresignedUrls, downloadFileFromBlob, getImageFormat, getImageMimeType };
